import { type ComputedRef, computed } from "vue";
import type { FormIoApplicationSubmission } from "@/components/RegistrationProcess/types/formio";
import { useCustomPriceFees } from "@/composables/authorization.js";
import { PROGRAM_STEP_DISABLED_STATUSES } from "@/constants";
import type { GoogleAddress } from "../types/address";

interface SessionWithApplicationFee {
  application_fee_in_cents?: number | null;
}

interface OrderWithApplicationFee {
  application_fee_in_cents?: number | null;
  status?: string | null;
}

interface FormioUserData {
  data: {
    firstname: string;
    lastname: string;
  };
}

export interface NoApplicationFeeSubmission {
  ipeds: string;
  ownerId: string;
  formioUserData: FormioUserData;
  applicationId: string;
  customerId: string;
  participantId: string;
  studentFormioPaymentSubmission: FormIoApplicationSubmission;
  currentAddress: GoogleAddress;
  permanentAddress: GoogleAddress;
  billingAddress: GoogleAddress;
}

export const useNoApplicationFeeFlow = (
  order: ComputedRef<OrderWithApplicationFee>,
  session: ComputedRef<SessionWithApplicationFee>,
) => {
  const feeIsLockedOnOrder = computed<boolean>(() =>
    PROGRAM_STEP_DISABLED_STATUSES.includes(order.value?.status ?? ""),
  );

  const noApplicationFeeInOrder = computed<boolean>(() => {
    // Orders should have their fee set to 0 explicitly
    return order.value?.application_fee_in_cents === 0;
  });

  const noApplicationFeeInSession = computed<boolean>(() => {
    // 0/null on session do count as no app fee flow
    return session.value?.application_fee_in_cents === 0 || session.value?.application_fee_in_cents === null;
  });

  const noApplicationFeeFlow = computed<boolean>(() => {
    // TODO: Remove feature flag once customized prices are the norm.
    if (!useCustomPriceFees.value) {
      return false;
    }
    // Once the amount is locked the session is no longer consulted, so repricing a session cannot
    // move an already-submitted application into (or out of) the no-fee flow.
    if (feeIsLockedOnOrder.value) {
      return noApplicationFeeInOrder.value;
    }
    return noApplicationFeeInOrder.value || noApplicationFeeInSession.value;
  });

  return { noApplicationFeeFlow };
};
